import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { LoadingCard } from '@components/utils/LoadingCard'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import {
  Alert,
  Card,
  Descriptions,
  Empty,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { FC, useEffect, useState } from 'react'
import {
  UserOnesignalId,
  userPushChannelMeta,
  userPushDeliveryVerdict,
} from '../../models/User'
import { fetchOnesignalIdsForUser } from '../../services/UserService'

const { Text } = Typography

interface Props {
  userId: number
}

const OneSignal: FC<Props> = ({ userId }) => {
  const [data, setData] = useState<UserOnesignalId[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)

  useEffect(() => {
    setIsLoading(true)
    setHasError(false)
    fetchOnesignalIdsForUser(userId)
      .then((result) => {
        setData(result)
      })
      // A failed fetch used to fall through to the empty state, which reads as
      // "this user has no registration" on a panel that now answers
      // deliverability.
      .catch(() => setHasError(true))
      .finally(() => setIsLoading(false))
  }, [userId])

  return (
    <Space orientation="vertical" className="w-full" size={8}>
      <PageTitle
        level={5}
        title={'Notification subscription'}
        style={{ margin: 0 }}
      />

      <Space orientation="vertical" className="w-full">
        {isLoading && <LoadingCard />}
        {!isLoading && hasError && (
          <Alert
            type="error"
            description="The notification subscriptions could not be loaded, so this panel says nothing about delivery."
          />
        )}
        {!isLoading && !hasError && data.length === 0 && (
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={'No notification subscription available'}
            />
          </Card>
        )}
        {!isLoading &&
          !hasError &&
          data.map((oneSignal) => {
            // Both undefined until the API answering them is deployed: the
            // whole column is then absent, rather than reporting an absence as
            // a negative or leaving a labelled cell empty.
            const channelMeta =
              oneSignal.channel !== undefined
                ? userPushChannelMeta[oneSignal.channel]
                : undefined
            const verdict = userPushDeliveryVerdict(oneSignal)
            const hasMigrationFields =
              channelMeta !== undefined || verdict !== undefined

            return (
              <Card key={oneSignal.id}>
                <Descriptions column={4} layout="vertical" size="small">
                  {/* LEGACY-ONESIGNAL - the channel and the verdict go away
                      with OneSignal, see PushRouting's removal notice. */}
                  {hasMigrationFields && (
                    <Descriptions.Item span={2} label="Channel">
                      {channelMeta !== undefined && (
                        <Tag color={channelMeta.color}>{channelMeta.label}</Tag>
                      )}
                      {verdict !== undefined && (
                        <Tooltip title={verdict.hint}>
                          <Tag
                            color={verdict.color}
                            style={
                              verdict.muted ? { opacity: 0.65 } : undefined
                            }
                          >
                            {verdict.label}
                          </Tag>
                        </Tooltip>
                      )}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item span={2} label="Status">
                    <Tag
                      color={oneSignal.subscribed === true ? 'green' : 'red'}
                      icon={
                        oneSignal.subscribed === true ? (
                          <CheckOutlined style={{ color: 'green' }} />
                        ) : (
                          <CloseOutlined style={{ color: 'red' }} />
                        )
                      }
                    >
                      {oneSignal.subscribed === true ? (
                        <>Accepted</>
                      ) : (
                        <>Rejected</>
                      )}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item
                    span={hasMigrationFields ? 4 : 2}
                    label={'Last updated on'}
                  >
                    <Text>
                      {' '}
                      {dayjs(oneSignal.updatedAt).format('DD/MM/YYYY HH:mm')}
                    </Text>
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )
          })}
      </Space>
    </Space>
  )
}

type OneSignalType = { OneSignal: typeof OneSignal }

export { OneSignal, type OneSignalType }
